import { useEffect, useState } from "react";
import { CheckCircleIcon, ExclamationCircleIcon, InformationCircleIcon, XIcon } from "@heroicons/react/outline";

export type ToastType = "error" | "success" | "info";

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
}

type Listener = (toasts: ToastItem[]) => void;

let toasts: ToastItem[] = [];
let listeners: Listener[] = [];
let nextId = 1;

function emit() {
  listeners.forEach(l => l(toasts));
}

export function showToast(message: string, type: ToastType = "error", durationMs = 6000): number {
  const id = nextId++;
  toasts = [...toasts, { id, message: message || "Something went wrong.", type }];
  emit();
  if (durationMs > 0)
    setTimeout(() => dismissToast(id), durationMs);
  return id;
}

export function dismissToast(id: number) {
  toasts = toasts.filter(t => t.id !== id);
  emit();
}

const TOAST_STYLES: Record<ToastType, string> = {
  error: "border-red/30 text-red",
  success: "border-green/30 text-green",
  info: "border-blue/30 text-blue",
};

function ToastIcon({ type }: { type: ToastType }) {
  const className = "w-5 h-5 shrink-0";
  if (type === "success") return <CheckCircleIcon className={className} />;
  if (type === "info") return <InformationCircleIcon className={className} />;
  return <ExclamationCircleIcon className={className} />;
}

export function Toaster() {
  const [items, setItems] = useState<ToastItem[]>(toasts);

  useEffect(() => {
    const listener: Listener = next => setItems(next);
    listeners.push(listener);
    setItems(toasts);
    return () => {
      listeners = listeners.filter(l => l !== listener);
    };
  }, []);

  if (items.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {items.map(item => (
        <div
          key={item.id}
          className={`flex items-start gap-2 bg-white border rounded-lg shadow-lg px-3 py-2.5 ${TOAST_STYLES[item.type]}`}
        >
          <ToastIcon type={item.type} />
          <div className="flex-1 min-w-0 text-sm font-gilroy text-gray-700 break-words">
            {item.message}
          </div>
          <button
            type="button"
            aria-label="Dismiss"
            className="shrink-0 text-gray-400 hover:text-gray-600"
            onClick={() => dismissToast(item.id)}
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
